// Employee Dashboard — WP plugin parity (Screen E1).
// Renders:
//   - Live-clock + Check-in/Check-out hero with WiFi pill
//   - Daily motivational quote
//   - "Good day, {first} 👋" greeting + pill actions
//   - 6 metric tiles (Present this month / Absent / Late Days / WFH Days / Leave Pending / Leave Approved)
//   - "Out Today" + "Working From Home" 2-column panel
//   - "Upcoming Holidays" + "My Projects" 2-column panel
//
// Phase 2 (Quantiv rebrand) is a restyle only. The check-in/check-out calls,
// the double-click guards, the confirm dialog, the 409 "already_in" handling and
// the clientIp self-diagnosis note are all untouched — what changed is colour,
// type and layout. The blue hero gradient, purple quote card and indigo/blue
// buttons are gone; semantic fills now come from tokens that pass WCAG AA.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { api, logout } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, EmptyState, Pill, Skeleton } from "../../Components/ui";
import ApplyLeaveModal from "../Leaves/ApplyLeaveModal";
import {
  eyebrowStyle, emptyPad, MetricTile, SectionHead, PillLink, primaryPillStyle,
} from "./dashboardUi";

const monthAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const dayAbbr   = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// Render a HH:MM[:SS] string as a nice 12-hour clock label in IST.
// The backend already stores HH:MM:SS in IST, so we only need to format —
// no further timezone math.
function fmtTimeIST(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh   = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

// The hero is a fixed dark panel in both themes — a near-black card with the
// brand red pushed into a corner glow. Every label on it is therefore measured
// against black (white @72% = 11.4:1, white @86% = 15.9:1).
const HERO_BG = [
  "radial-gradient(120% 150% at 88% -10%, rgba(227,30,36,0.45) 0%, rgba(227,30,36,0.12) 40%, rgba(227,30,36,0) 72%)",
  "linear-gradient(135deg, #0A0A0B 0%, #000000 100%)",
].join(", ");

// Solid fills used on the hero. White text on each clears 4.5:1, so they are
// safe for the button labels as well as the pills. See index.css --fill-*.
const heroBtnBase = {
  color: "#FFFFFF", border: "1px solid transparent",
  padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
  minWidth: 158, fontFamily: "inherit",
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
};

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [today, setToday]   = useState(null);   // /api/attendance/today response
  const [hr, setHr]         = useState(null);   // /api/hr/dashboard response
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState(false);
  const [clock, setClock]   = useState(new Date());
  const [applyOpen, setApplyOpen] = useState(false);
  const username   = localStorage.getItem("username") || "there";
  const firstName  = (username || "").split(" ")[0];

  useShellHeader({ title: "" }, []);

  // Live clock — ticks every second
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useRef(async () => {
    setLoad(true);
    try {
      const [t, d] = await Promise.all([
        api("/attendance/today"),
        api("/hr/dashboard").catch(() => null),
      ]);
      setToday(t); setHr(d);
    } catch (e) {
      if (e.status === 401) { logout(); navigate("/login"); return; }
      toast({ title: e.message, status: "error" });
    } finally {
      setLoad(false);
    }
  }).current;
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function checkin() {
    if (busy) return; // double-click guard while a previous click is in-flight
    setBusy(true);
    try {
      const r = await api("/attendance/checkin", { method: "POST" });
      toast({
        title: "✓ Checked in",
        description: r.message || "Have a great day!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      load();
    } catch (e) {
      // Backend returns 409 with code "already_in" if it's a repeat — surface the time clearly.
      // For the office-WiFi block (code "wrong_network") the backend includes the
      // IP it detected — show it so the employee can self-diagnose (mobile data /
      // IPv6 / wrong network) instead of guessing.
      const ipNote = e.data?.clientIp
        ? ` (We detected your network IP as ${e.data.clientIp}. If you're in the office, turn off mobile data and reconnect to the office WiFi, then try again.)`
        : "";
      toast({
        title: e.data?.code === "already_in" ? "Already checked in" : "Check-in failed",
        description: (e.data?.message || e.message || "Please refresh and try again.") + ipNote,
        status: e.data?.code === "already_in" ? "info" : "error",
        duration: 9000,
        isClosable: true,
      });
    } finally { setBusy(false); }
  }
  async function checkout() {
    if (busy) return;
    // Confirm — accidental check-out before end-of-day is a real risk.
    if (!window.confirm("Check out for the day? Your work hours will be locked in once you confirm.")) return;
    setBusy(true);
    try {
      const r = await api("/attendance/checkout", { method: "POST" });
      toast({
        title: "✓ Checked out",
        description: r.message || "See you tomorrow!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      load();
    } catch (e) {
      toast({
        title: "Check-out failed",
        description: e.data?.message || e.message || "Please refresh and try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally { setBusy(false); }
  }

  if (loading || !today) {
    return <div style={{ padding: 16 }}><Skeleton.Card /></div>;
  }

  // Backend returns camelCase (Mongoose doc + camelCase response keys); tolerate
  // both shapes so legacy snake_case payloads keep working too.
  const onOfficeIp     = !!(today.isOfficeIp ?? today.is_office_ip);
  const noIpsSet       = !!(today.noIpsConfigured ?? today.no_ips_set);
  const wfhApprovedToday = !!(today.wfhToday ?? today.wfh_approved);
  const checkInVal  = today.record?.checkIn  ?? today.record?.check_in;
  const checkOutVal = today.record?.checkOut ?? today.record?.check_out;
  const todayStatus = today.record?.status ?? null;
  const wasLateToday  = todayStatus === "late";
  const hasCheckedIn  = !!checkInVal;
  const hasCheckedOut = !!checkOutVal;

  const clockTime = clock.toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateLabel = `${dayAbbr[clock.getDay()]}, ${clock.getDate()} ${monthAbbr[clock.getMonth()]} ${clock.getFullYear()}`;

  const statusText = hasCheckedOut
    ? `Day done. Have a good evening.`
    : hasCheckedIn
      ? `You're checked in for the day.`
      : "Tap Check In to mark your attendance.";

  // 6 metric tiles from hr data
  const att = hr?.attendanceThisMonth || {};
  const lv  = hr?.leaves || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ── Late banner — shown when today's check-in was past the late cutoff (10:00 + 15m = 10:15 IST by default). */}
      {wasLateToday && (
        <div role="alert" style={{
          background: "var(--tone-warning-bg)",
          border: "1px solid var(--tone-warning-fg)",
          borderRadius: 14,
          padding: "12px 18px",
          color: "var(--tone-warning-fg)",
          fontSize: 13,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700 }}>You were marked late today</div>
            <div style={{ fontSize: 12, marginTop: 2 }}>Check-in at <b className="tabular">{fmtTimeIST(checkInVal)}</b> exceeded the late cutoff (10:15 AM IST).</div>
          </div>
        </div>
      )}

      {/* ── Hero check-in/out card ───────────────────────────────── */}
      <div style={{
        background: HERO_BG,
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 16,
        padding: "26px 28px",
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 24,
      }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ ...eyebrowStyle, color: "rgba(255,255,255,0.72)" }}>{dateLabel}</div>
          <div className="tabular" style={{ fontSize: 46, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, margin: "8px 0 6px" }}>{clockTime}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.86)" }}>{statusText}</div>
          {/* WiFi pill — solid fill + white label, so it reads on the dark hero. */}
          <div style={{ marginTop: 12 }}>
            {noIpsSet ? null : onOfficeIp ? (
              <HeroPill fill="var(--fill-success)">● On Office WiFi</HeroPill>
            ) : wfhApprovedToday ? (
              <HeroPill fill="var(--fill-violet)">🏠 Approved WFH today</HeroPill>
            ) : (
              <HeroPill fill="var(--fill-danger)">● Not on Office WiFi</HeroPill>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
          {/* ONE primary button that morphs through the day's three states:
              not-yet-checked-in → checked-in (button now says Check Out)
              → checked-out (button gone; pill says Day done). The day's
              actual times are shown as small sub-labels below the button
              so the employee can see what timestamp got recorded.
              The "Go to Portal" link sits beside it as the secondary action —
              outlined rather than filled, so the attendance action stays first. */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <a
              href="https://workspace.quantivmedia.com"
              target="_blank"
              rel="noopener noreferrer"
              title="Open the Quantiv Media workspace in a new tab"
              style={{
                ...heroBtnBase,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.30)",
                textDecoration: "none",
              }}
            >Go to Portal <span aria-hidden="true">↗</span></a>
          {!hasCheckedIn && (
            <button
              onClick={checkin}
              disabled={busy}
              style={{
                ...heroBtnBase,
                background: "var(--fill-success)",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >{busy ? "Checking in…" : "Check In"}</button>
          )}
          {hasCheckedIn && !hasCheckedOut && (
            <button
              onClick={checkout}
              disabled={busy}
              style={{
                ...heroBtnBase,
                background: "var(--fill-warning)",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >{busy ? "Checking out…" : "Check Out"}</button>
          )}
          {hasCheckedOut && (
            <div title="Day done. See you tomorrow!" style={{
              ...heroBtnBase,
              background: "rgba(255,255,255,0.16)",
              border: "1px solid rgba(255,255,255,0.30)",
              padding: "12px 18px", fontSize: 14,
            }}>
              <span aria-hidden="true" style={{
                width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.92)",
                color: "#0A0A0B",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700,
              }}>✓</span>
              Day done
            </div>
          )}
          </div>

          {/* Sub-labels showing the actual recorded times under the button. */}
          {(hasCheckedIn || hasCheckedOut) && (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.86)", textAlign: "right", lineHeight: 1.6 }}>
              {hasCheckedIn && <div>In: <span className="tabular" style={{ fontWeight: 600 }}>{fmtTimeIST(checkInVal)}</span></div>}
              {hasCheckedOut && <div>Out: <span className="tabular" style={{ fontWeight: 600 }}>{fmtTimeIST(checkOutVal)}</span></div>}
            </div>
          )}
        </div>
      </div>

      {/* ── Daily quote ────────────────────────────────────────── */}
      {hr?.dailyQuote && (
        <Card padding="lg" style={{ borderLeft: "3px solid var(--accent-primary)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <span aria-hidden="true" style={{
              fontSize: 40, lineHeight: 0.8, color: "var(--accent-text)",
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}>{"“"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontStyle: "italic", fontSize: 15, lineHeight: 1.55, color: "var(--text-primary)" }}>{hr.dailyQuote.q}</div>
              <div style={{ ...eyebrowStyle, marginTop: 10, color: "var(--accent-text)" }}>— {hr.dailyQuote.a}</div>
            </div>
          </div>
        </Card>
      )}

      {/* ── Greeting + pill actions ─────────────────────────────── */}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={eyebrowStyle}>
            {localStorage.getItem("role") === "SUPER_ADMIN" ? "Admin" : "Team Member"}
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.025em", color: "var(--text-primary)", margin: "6px 0 0", lineHeight: 1.1 }}>
            Good day, {firstName} <span aria-hidden="true">👋</span>
          </h2>
        </div>
        <nav style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <PillLink to="/attendance" icon="🕐" label="My Attendance" />
          <PillLink to="/leaves"     icon="📅" label="My Leaves" />
          <button type="button" onClick={() => setApplyOpen(true)} style={primaryPillStyle}>
            + Apply for Leave
          </button>
        </nav>
      </header>

      {/* ── 6 metric tiles ─────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        <MetricTile tone="success" label="Present this month" value={att.present || 0} />
        <MetricTile tone="danger"  label="Absent"             value={att.absent  || 0} />
        <MetricTile tone="warning" label="Late Days"          value={att.late    || 0} />
        <MetricTile tone="violet"  label="WFH Days"           value={att.wfh     || 0} />
        <MetricTile tone="warning" label="Leave Pending"      value={lv.pending  || 0} />
        <MetricTile tone="success" label="Leave Approved"     value={lv.approved || 0} />
      </div>

      {/* ── Out Today / WFH ──────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="❌" title="Out Today" />
          <PersonList list={hr?.absentToday || []} kind="out" />
        </Card>
        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="🏠" title="Working From Home" />
          <PersonList list={hr?.wfhToday || []} kind="wfh" />
        </Card>
      </div>

      {/* ── Upcoming Holidays / My Projects ──────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="⭐" title="Upcoming Holidays" />
          {(hr?.upcomingHolidays || []).length === 0 ? (
            <EmptyState description="No upcoming holidays." style={emptyPad} />
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {hr.upcomingHolidays.map((h) => (
                <li key={h._id} style={rowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{h.title}</span>
                  <span className="tabular" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{new Date(h.holidayDate).toISOString().slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card padding="none" style={{ overflow: "hidden" }}>
          <SectionHead icon="💼" title="My Projects" />
          {(hr?.myProjects || []).length === 0 ? (
            <EmptyState description="No active projects." style={emptyPad} />
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {hr.myProjects.map((p) => (
                <li key={p._id} style={rowStyle}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{p.name}</span>
                  <span style={{ ...eyebrowStyle, fontSize: 10 }}>{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <ApplyLeaveModal open={applyOpen} onClose={() => setApplyOpen(false)} onSubmitted={() => { setApplyOpen(false); load(); }} />
    </div>
  );
}

/* ── Local presentation pieces ─────────────────────────────────── */

// Solid-fill pill for use on the dark hero only (white on each --fill-* is >=4.69:1).
function HeroPill({ fill, children }) {
  return (
    <span style={{
      background: fill, color: "#FFFFFF",
      padding: "5px 12px", borderRadius: 9999, fontSize: 12, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 6,
    }}>{children}</span>
  );
}

function PersonList({ list, kind }) {
  if (!list.length) {
    return <EmptyState description={kind === "out" ? "Everyone's in." : "No one is WFH today."} style={emptyPad} />;
  }
  // Avatars use the tinted tone pair rather than a white-on-colour disc: the
  // old #DC2626/#7C3AED discs put white initials at ~4.0:1 and ~5.3:1.
  const tone = kind === "out"
    ? { bg: "var(--tone-danger-bg)", fg: "var(--tone-danger-fg)" }
    : { bg: "var(--tone-violet-bg)", fg: "var(--tone-violet-fg)" };
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {list.map((s, i) => {
        const fn = (s.firstName || "").trim();
        const ln = (s.lastName || "").trim();
        const initials = (fn[0] || "") + (ln[0] || "");
        const name = `${fn} ${ln}`.trim() || s.username;
        const dept = s.team?.name || s.team || "";
        return (
          <li key={`${kind}-${i}-${s._id || ""}`} style={{ padding: "11px 18px", borderTop: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", flex: "0 0 auto",
              background: tone.bg, color: tone.fg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700,
            }}>{initials.toUpperCase() || "?"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{name}</div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{dept}</div>
            </div>
            {kind === "out" ? (
              <Pill tone={s.reason === "leave" ? "warning" : "danger"}>
                {s.reason === "leave" ? "🏖 On leave" : "❌ Absent"}
              </Pill>
            ) : (
              <Pill tone="violet">🏠 WFH</Pill>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const rowStyle = {
  padding: "11px 18px", borderTop: "1px solid var(--border-subtle)",
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
};
